// @ts-strict-ignore
import {
  AfterViewChecked,
  Component,
  ElementRef,
  Input,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectorRef,
} from '@angular/core';
import { uniqueId } from 'lodash-es';
import { timer } from 'rxjs';
import { TranslationService } from '../../../../services/translation-service';
import { GlobalIcon } from '../icon/icon.constants';
import { TranslationKey } from '@tolgee/ngx';

export interface TabListTab {
  label: TranslationKey;
  ariaControls?: string;
  badge?: string;
  badgeStyleClass?: string;
  command?: (event?: any) => void;
  disabled?: boolean;
  extra?: string;
  error?: boolean;
  hasRequired?: boolean;
  id?: string;
  visible?: boolean;
}

/**
 * @deprecated Remove component when the new cv tab list component replaces `<app-tab-list>` in board copy dialog,
 * board move dialog, organisations, board settings, transition target layout, unified action editor, connection tree
 * sidebar, and navigation tree.
 */
@Component({
    selector: 'app-tab-list',
    templateUrl: './tab-list.component.html',
    styleUrls: ['./tab-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class TabListComponent implements AfterViewChecked {
  @Input() activeTab: TabListTab;
  @Input() ariaControlsInput: string;
  @Input() centerTabs: boolean = false;
  @Input() class: string;
  @Input() scrollable: boolean = false;
  @Input() tabs: TabListTab[];
  @Input() tabListId: string = uniqueId('tab-list');
  @ViewChild('tabList') tabList: ElementRef;

  get firstTabIndex() {
    return 0;
  }

  get focusedTabIndex() {
    const children = Array.from(this.tabList.nativeElement.children);
    return children.findIndex((child) => child === document.activeElement);
  }

  get hideScrollLeft() {
    return this._tabsOverflow?.left === 0;
  }

  get hideScrollRight() {
    return this._tabsOverflow?.right === 0;
  }

  get lastTabIndex() {
    return this.tabs.length - 1;
  }

  Icon = GlobalIcon;

  private _didScroll: boolean;
  private _tabsOverflow: {
    left: number;
    right: number;
  };

  constructor(private _cdr: ChangeDetectorRef, private _translationService: TranslationService) {}

  calculateTabsOverflow() {
    const tabEls: HTMLButtonElement[] = Array.from(this.tabList.nativeElement.children);

    const tabListRect = this.tabList.nativeElement.getBoundingClientRect();
    const firstTabRect = tabEls[0].getBoundingClientRect();
    const lastTabRect = tabEls[tabEls.length - 1].getBoundingClientRect();

    this._tabsOverflow = {
      left: firstTabRect.left < tabListRect.left ? firstTabRect.left - tabListRect.left : 0,
      right: lastTabRect.right > tabListRect.right ? lastTabRect.right - tabListRect.right : 0,
    };
  }

  ngAfterViewChecked() {
    if (this.scrollable) {
      this.calculateTabsOverflow();
      this._cdr.detectChanges();
    }
  }

  onArrowLeftDown(event: KeyboardEvent) {
    event.preventDefault();
    const newIndex = this.focusedTabIndex === this.firstTabIndex ? this.lastTabIndex : this.focusedTabIndex - 1;
    this._moveFocusToIndex(newIndex);
  }

  onArrowRightDown(event: KeyboardEvent) {
    event.preventDefault();
    const newIndex = this.focusedTabIndex === this.lastTabIndex ? this.firstTabIndex : this.focusedTabIndex + 1;
    this._moveFocusToIndex(newIndex);
  }

  onScroll() {
    if (this.scrollable && !this._didScroll) {
      this._didScroll = true;
      timer(100).subscribe(() => {
        this.calculateTabsOverflow();
        this._didScroll = false;
      });
    }
  }

  onScrollClick(direction: 'left' | 'right') {
    const tabListRect = this.tabList.nativeElement.getBoundingClientRect();
    const tabEls: HTMLButtonElement[] = Array.from(this.tabList.nativeElement.children);
    let closestTabOverflowing: HTMLButtonElement;
    let firstTabElRect: DOMRect;
    let offsetLeft: number;
    const tabIsFullyVisible = (tabElRect: DOMRect) =>
      direction === 'right' ? tabElRect.right <= tabListRect.right : tabElRect.left >= tabListRect.left;

    if (direction === 'right') {
      firstTabElRect = tabEls[0].getBoundingClientRect();
      tabEls.reverse();
    }

    for (const tabEl of tabEls) {
      const tabElRect = tabEl.getBoundingClientRect();

      if (tabIsFullyVisible(tabElRect)) {
        break;
      }

      closestTabOverflowing = tabEl;
      offsetLeft =
        direction === 'right'
          ? tabElRect.right - firstTabElRect.left - tabListRect.width + 2
          : closestTabOverflowing.offsetLeft - 1;
    }

    if (closestTabOverflowing) {
      this._scrollTo(offsetLeft);
    }
  }

  onTabClick(tab: TabListTab) {
    if (tab.disabled) {
      return;
    }

    this.activeTab = tab;
    tab.command?.({ item: tab });
  }

  onTabFocus() {
    this._adjustScroll(this.focusedTabIndex);
  }

  tabContent(tab: TabListTab) {
    return this._translate(tab.label) + (tab.hasRequired ? '*' : '') + (tab.extra ?? '');
  }

  tabId(tab: TabListTab) {
    return tab.id ? tab.id : 'tab-' + tab.label;
  }

  tabTabindex(tab: TabListTab) {
    return tab === this.activeTab ? 0 : -1;
  }

  private _adjustScroll(focusedIndex: number): void {
    const tabEl: HTMLButtonElement = this.tabList.nativeElement?.children[focusedIndex];

    if (!tabEl) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            this._scrollTo(tabEl.offsetLeft - 1); // -1 to compensate for focus outline
          }
          observer.disconnect();
        });
      },
      { threshold: 1 }
    );
    observer.observe(tabEl);
  }

  private _moveFocusToIndex(index: number) {
    const tabElement = this.tabList.nativeElement?.children[index];
    tabElement?.focus();
  }

  private _scrollTo(offsetLeft: number) {
    this.tabList.nativeElement.scrollTo({
      top: 0,
      left: offsetLeft,
      behavior: 'smooth',
    });
  }

  private _translate(stringId: TranslationKey): string {
    return this._translationService.translateToUserLanguage(stringId);
  }
}
